<template>
	<div class="row align-items-end">
		<!-- Column -->
		<div class="col-6 col-md-3 mb-1 mb-md-0">
			<e-select
				v-model="internalColumn"
				:options="availableColumns"
				:searchable="true"
				:allow-empty="false"
				:show-labels="true"
				track-by="key"
				label="label"
			/>
		</div>
		<!-- Operator -->
		<div class="col-6 col-md-3 mb-1 mb-md-0">
			<e-select
				v-model="internalOperator"
				:options="availableOperators"
				:searchable="true"
				:sortable="false"
				:allow-empty="false"
				:show-labels="true"
				track-by="key"
				label="label"
			/>
		</div>
		<!-- Input -->
		<div :class="onlineMode ? 'col-8 col-md-4' : 'col-10 col-md-5'" class="mb-1 mb-md-0">
			<e-select
				v-if="hasSearchValues"
				track-by="code"
				label="label"
				:options="searchValues"
				:searchable="true"
				:allow-empty="false"
				:show-labels="true"
				:search="true"
				v-model="internalValueObject"
				:placeholder="$t('global.rechercher')"
				@input="updateInternalValue"
			>
				<template slot="option" slot-scope="{ option }"><span v-html="cleanHTML(option.label)"></span></template>
				<template slot="singleLabel" slot-scope="{ option }"><span v-html="cleanHTML(option.label)"></span></template>
				<template slot="noOptions">{{ $t('global.list_empty') }}</template>
			</e-select>
			<b-form-input
				v-else
				ref="searchfield"
				v-model="internalValue"
				type="text"
				:placeholder="getTrad('global.recherchez_tableau')"
				:debounce="onlineMode ? '0' : '500'"
				@keydown.enter="onEnterSearch"
			/>
		</div>
		<!-- Bouton rechercher (onlineMode) -->
		<div v-if="onlineMode" class="col-2 col-md-1 px-md-0 mb-1 mb-md-0">
			<b-button
				block
				variant="primary"
				@click="submitSearch"
			>
				<font-awesome-icon :icon="['fal', 'search']"/>
			</b-button>
		</div>
		<!-- Bouton vider le champs -->
		<div class="col-2 col-md-1 px-md-0 mb-1 mb-md-0">
			<b-button
				block
				:disabled="!internalValue"
				@click="clearSearch"
			>
				<font-awesome-icon :icon="['fal', 'trash-alt']"/>
			</b-button>
		</div>
	</div>
</template>

<script type="text/javascript">
	import TableSearchMixin from '@/mixins/TableSearch.js'
    import xss from 'xss'

	export default {
		name: 'TableSearchInput',
		mixins: [TableSearchMixin],
		props: {
			searchColumn: String,
			searchOperator: String,
			searchValue: String,
			columns: Array,
			typeSearch: Object,
			onlineMode: {
				type: Boolean,
				default: false
			}
		},
		data: () => ({
			availableOperators: [],
			allowedOperators: [
				'isEqualTo',
				'isNotEqualTo',
				'isLowerThan',
				'isLowerOrEqualThan',
				'isGreaterThan',
				'isGreaterOrEqualThan',
				'mustContain',
				'mustNotContain',
				'beginWith',
				'doesntBeginWith',
				'endWith',
				'doesntEndWith',
				'isEmpty',
				'isNotEmpty',
				'isTrue',
				'isFalse'
			],
			searchValues: [],
			internalValueObject: null,
			localValue: '',
			localColumn: null,
			localOperator: null
		}),
		computed: {
			internalColumn: {
				get() {
					if (this.onlineMode && this.localColumn) {
						return this.localColumn
					}
					return this.availableColumns.find(col => (col.key === this.searchColumn))
				},
				set(val) {
					this.displayValues(val.key)
					if (this.onlineMode) {
						this.localColumn = val
					} else {
						this.$emit('update:searchColumn', val.key)
					}
				}
			},
			internalOperator: {
				get() {
					if (this.onlineMode && this.localOperator) {
						return this.localOperator
					}
					return this.availableOperators.find(op => (op.key === this.searchOperator))
				},
				set(val) {
					if (this.onlineMode) {
						this.localOperator = val
					} else {
						this.$emit('update:searchOperator', val.key)
					}
				}
			},
			internalValue: {
				get() {
					if (this.onlineMode) {
						return this.localValue
					}
					return this.searchValue
				},
				set(val) {
					if (this.onlineMode) {
						this.localValue = val
					} else {
						this.updateInternalValueObject(val)
						this.$emit('update:searchValue', val)
					}
				}
			},
			availableColumns() {
				return [{
					key: 'stringified',
					label: this.$t('table.general.search_stringified')
				}]
				.concat(this.columns)
			},
	        hasSearchValues() {
	            if(!this.typeSearch) return false
	            return Object.prototype.hasOwnProperty.call(this.typeSearch, this.internalColumn.key)
	        }
		},
		created() {
			const operators = this.getConfig('action_filter')

			this.availableOperators = operators
			.filter(op => {
				return this.allowedOperators.includes(op.action)
			})
			.map(op => {
				return {
					key: op.action,
					label: this.$t(`filtres.${op.label}`)
				}
			})
		},
		methods: {
			focus() {
				this.$refs.searchfield.$el.focus()
			},
			/**
			 * Synchronise les valeurs locales vers le parent et émet l'événement submitSearch.
			 */
			submitSearch() {
				this.syncToParent()
				this.$emit('submitSearch')
			},
			/**
			 * Gère la touche Entrée dans le champ de recherche (soumet en mode online).
			 */
			onEnterSearch() {
				if (this.onlineMode) {
					this.submitSearch()
				}
			},
			/**
			 * Vide le champ de recherche et soumet si en mode online.
			 */
			clearSearch() {
				this.internalValue = ''
				if (this.onlineMode) {
					this.submitSearch()
				}
			},
			/**
			 * Émet les valeurs locales (colonne, opérateur, valeur) vers le composant parent via les events .sync.
			 */
			syncToParent() {
				this.$emit('update:searchValue', this.onlineMode ? this.localValue : this.searchValue)
				if (this.localColumn) {
					this.$emit('update:searchColumn', this.localColumn.key)
				}
				if (this.localOperator) {
					this.$emit('update:searchOperator', this.localOperator.key)
				}
			},
			async displayValues(key) {
				this.searchValues = []
	            if(Object.prototype.hasOwnProperty.call(this.typeSearch, key)) {
	                this.searchValues = await this[this.typeSearch[key]]()
	                this.internalValueObject = null
	                this.updateInternalValue(null)
	            }
			},
	        cleanHTML(input) {
	            return xss(input)
	        },
	        updateInternalValue(val) {
	        	this.internalValue = val ? val.code : ''
	        },
	        updateInternalValueObject(val) {
	        	if(this.searchValues.length > 0) {
	        		this.internalValueObject = this.searchValues.find(v => v.code == val)
	        	}
	        }
		}
	}
</script>
